import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Building2, AlertTriangle, DollarSign, TrendingDown, TrendingUp } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtCurrency = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(v || 0);

function LineItem({ label, amount, bold, indent }) {
  return (
    <div className={`flex justify-between py-1.5 ${bold ? 'font-semibold border-t border-gray-200 pt-2' : ''} ${indent ? 'pl-4' : ''}`}>
      <span className="text-gray-700">{label}</span>
      <span className={amount < 0 ? 'text-red-600' : 'text-gray-900'}>{fmtCurrency(amount)}</span>
    </div>
  );
}

export default function SharedReportPage() {
  const { token } = useParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchReport = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/reports/shared/${token}`);
        setReport(res.data);
      } catch (err) {
        if (err.response?.status === 410) setError('This shared report has expired.');
        else if (err.response?.status === 404) setError('Shared report not found.');
        else setError('Failed to load report.');
      } finally { setLoading(false); }
    };
    fetchReport();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          <p className="text-gray-500 text-sm">Loading report...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8 text-center max-w-md">
          <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-gray-900">{error}</h2>
          <p className="text-gray-500 text-sm mt-2">Please contact the report owner for a new link.</p>
        </div>
      </div>
    );
  }

  const { report_type, company_name, data, created_at, view_count } = report;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="shared-report-page">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-gray-900 flex items-center justify-center">
              <Building2 className="w-4 h-4 text-white" />
            </div>
            <div>
              <h1 className="font-semibold text-gray-900">BusinessHub</h1>
              <p className="text-xs text-gray-500">Shared Report</p>
            </div>
          </div>
          <div className="text-right text-xs text-gray-500">
            <p>{company_name}</p>
            <p>Shared {new Date(created_at).toLocaleDateString()} — {view_count} views</p>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-6 space-y-6">
        {report_type === 'overview' && <OverviewView data={data} companyName={company_name} />}
        {report_type === 'pnl' && <PnlView data={data} companyName={company_name} />}
        {report_type === 'balance_sheet' && <BalanceSheetView data={data} companyName={company_name} />}
      </main>

      <footer className="text-center py-6 text-xs text-gray-400">
        Generated by BusinessHub
      </footer>
    </div>
  );
}

function OverviewView({ data, companyName }) {
  return (
    <div className="space-y-4" data-testid="shared-overview">
      <h2 className="text-xl font-semibold text-gray-900">Financial Overview — {companyName}</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Revenue" value={fmtCurrency(data.total_revenue)} icon={DollarSign} positive />
        <StatCard label="Expenses" value={fmtCurrency(data.total_expenses)} icon={TrendingDown} />
        <StatCard label="Net Profit" value={fmtCurrency(data.net_profit)} icon={TrendingUp} positive={data.net_profit >= 0} />
        <StatCard label="Liabilities" value={fmtCurrency(data.total_liabilities)} icon={TrendingDown} />
      </div>
    </div>
  );
}

function PnlView({ data, companyName }) {
  return (
    <div className="space-y-4" data-testid="shared-pnl">
      <h2 className="text-xl font-semibold text-gray-900">Profit & Loss — {companyName}</h2>
      <p className="text-sm text-gray-500">{data.period?.start} to {data.period?.end}</p>
      <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
        <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">Revenue</h3>
        {data.revenue_items?.map((item, i) => <LineItem key={i} label={item.category} amount={item.amount} indent />)}
        <LineItem label="Total Revenue" amount={data.revenue} bold />

        <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mt-4">Expenses</h3>
        {data.expense_items?.map((item, i) => <LineItem key={i} label={item.category} amount={item.amount} indent />)}
        <LineItem label="Total Expenses" amount={data.expenses} bold />

        <div className="border-t-2 border-gray-300 pt-3 mt-4">
          <LineItem label="Net Profit" amount={data.net_profit} bold />
        </div>
        {data.liabilities > 0 && <LineItem label="Outstanding Liabilities" amount={data.liabilities} indent />}
      </div>
    </div>
  );
}

function BalanceSheetView({ data, companyName }) {
  return (
    <div className="space-y-4" data-testid="shared-balance-sheet">
      <h2 className="text-xl font-semibold text-gray-900">Balance Sheet — {companyName}</h2>
      <p className="text-sm text-gray-500">As of {data.as_of_date}</p>
      <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
        <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">Assets</h3>
        <LineItem label="Cash" amount={data.assets?.cash} indent />
        <LineItem label="Accounts Receivable" amount={data.assets?.accounts_receivable} indent />
        <LineItem label="Total Assets" amount={data.assets?.total} bold />

        <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mt-4">Liabilities</h3>
        <LineItem label="Owner Payable" amount={data.liabilities?.owner_payable} indent />
        <LineItem label="Payments Made" amount={data.liabilities?.payments_made} indent />
        <LineItem label="Total Liabilities" amount={data.liabilities?.total} bold />

        <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mt-4">Equity</h3>
        <LineItem label="Retained Earnings" amount={data.equity?.retained_earnings} indent />
        <LineItem label="Total Equity" amount={data.equity?.total} bold />
      </div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, positive }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm text-gray-500">{label}</p>
        <Icon className={`w-4 h-4 ${positive ? 'text-blue-600' : 'text-gray-400'}`} />
      </div>
      <p className="text-lg font-semibold">{value}</p>
    </div>
  );
}
